/**
 * 技术支持一键登录
 *
 * 技术支持在管理后台输入访客 user_id 拿到票据后，跳到「坐席后台地址?ticket=xxx」，
 * 这里用票据换登录态，免去向客户索要账号密码。
 *
 * 票据 10 秒过期且只能用一次，所以在应用挂载前就地换取，中间不做任何确认交互。
 * 无论当前是否已登录都执行——技术支持常以自己的账号开着后台，这一步要的就是切成客户账号。
 */
import { queryAuthLoginByTicket } from '@/api';
import { useUserStore, type UserInfo } from '@/stores/user';
import { loginTicket } from '@/utils/ticketParam';

/** URL 上是否带着票据。没有票据时入口保持原有的同步挂载路径不变 */
export const hasLoginTicket = (): boolean => !!loginTicket;

/**
 * 用票据登录。
 *
 * 返回 true 表示已触发整页重载，调用方不要再挂载应用实例。
 */
export const loginByTicket = async (): Promise<boolean> => {
  const ticket = loginTicket;
  if (!ticket) return false;

  /** 票据已由 ticketParam 在 router 创建前抹掉，这里的地址栏就是干净地址 */
  const cleanUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  try {
    const data = await queryAuthLoginByTicket(ticket);
    if (!data?.token) return false;
    useUserStore().setLogin({ token: data.token, user: data.user as unknown as UserInfo });
    /**
     * 整页重载而非路由跳转：技术支持可能正以自己的账号登录着，只换 token 会留下
     * 上一个账号的 store 缓存、IM 连接与顶部标签页，重载能让它们全部按新账号重建。
     */
    window.location.replace(cleanUrl);
    return true;
  } catch {
    /**
     * 有意的空 catch：票据过期等文案由响应拦截器统一弹出，这里只需要不再继续重载。
     * 刻意不清已有登录态——技术支持若在同一浏览器登录着自己的账号，清 token 会把
     * 他其它标签页一起挤掉线；本来就未登录的场景，由路由守卫照常送去登录页。
     */
    return false;
  }
};
